import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ConflictSolution {
  type: "remove_artist" | "remove_custom_subject";
  data: {
    artist_name?: string;
    stage_name?: string;
    subject?: string;
    artist?: string;
    stage?: string;
    location?: string;
  };
}

interface Conflict {
  artist: string;
  stage: string;
  timeSlot: string;
  solution: string;
  solutionAction: ConflictSolution;
}

interface AiErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  errorMessage: string;
  suggestions?: string[];
  conflicts?: Conflict[];
  onSolve?: (conflict: Conflict) => void;
  onIgnore?: (conflict: Conflict) => void;
}

export function AiErrorDialog({
  open,
  onOpenChange,
  errorMessage,
  suggestions = [],
  conflicts = [],
  onSolve,
  onIgnore,
}: AiErrorDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Unable to generate schedule
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p className="text-foreground">{errorMessage}</p>

              {conflicts.length > 0 && (
                <div>
                  <p className="mb-3 font-semibold text-foreground">Unable to assign:</p>
                  <ScrollArea className="max-h-[400px] pr-4">
                    <div className="space-y-3">
                      {conflicts.map((conflict, index) => (
                        <div
                          key={index}
                          className="space-y-3 rounded-lg border border-border bg-muted/50 p-4"
                        >
                          <div>
                            <p className="font-medium text-foreground">
                              {conflict.artist} - {conflict.stage} {conflict.timeSlot}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Solution: {conflict.solution}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                onSolve?.(conflict);
                                onOpenChange(false);
                              }}
                            >
                              Solve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                onIgnore?.(conflict);
                                onOpenChange(false);
                              }}
                            >
                              Ignore (don't schedule)
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {suggestions.length > 0 && conflicts.length === 0 && (
                <div>
                  <p className="mb-2 font-semibold text-foreground">Suggestions to resolve:</p>
                  <ul className="list-inside list-disc space-y-1 text-sm">
                    {suggestions.map((suggestion, index) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
